import React, { useState, useEffect } from "react";
import { useAppContext } from "../../functions/ContextAPI";
import { toast } from "react-toastify";
import {
  CloudArrowUpIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  ArrowDownTrayIcon,
  EyeIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import DocumentPreview from "../../components/DocumentPreview";

/**
 * Upload Documents Page Component
 * Allows representatives to upload documents with title and file
 */
const UploadDocuments = () => {
  const context = useAppContext();
  const [title, setTitle] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadedDocuments, setUploadedDocuments] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewDocument, setPreviewDocument] = useState(null);

  const selectedProgramme = context.selectedProgramme;
  const accreditationId = selectedProgramme?.accreditationId || selectedProgramme?._id;
  const isStage6 = selectedProgramme?.accreditationData?.stage === 6;

  // Fetch uploaded documents on mount
  useEffect(() => {
    if (accreditationId) {
      fetchDocuments();
    }
  }, [accreditationId]);

  const fetchDocuments = async () => {
    setIsLoading(true);
    try {
      const { getRepresentativeDocuments } = await import("../../service/templateService");
      const response = await getRepresentativeDocuments(accreditationId);
      setUploadedDocuments(response.documents || []);
    } catch (error) {
      console.error("Error fetching documents:", error);
      toast.error("Failed to load uploaded documents");
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file size (max 50MB)
      if (file.size > 50 * 1024 * 1024) {
        toast.error("File size must be less than 50MB");
        return;
      }

      // Validate file type
      const allowedTypes = [
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'image/jpeg',
        'image/png',
        'image/jpg',
      ];

      if (!allowedTypes.includes(file.type)) {
        toast.error("Only PDF, Word, Excel, and image files are allowed");
        return;
      }

      setSelectedFile(file);
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();

    if (!title.trim()) {
      toast.error("Please enter a document title");
      return;
    }

    if (!selectedFile) {
      toast.error("Please select a file to upload");
      return;
    }

    setIsUploading(true);

    try {
      const formData = new FormData();
      formData.append('title', title.trim());
      formData.append('file', selectedFile);

      const { uploadRepresentativeDocument } = await import("../../service/templateService");
      await uploadRepresentativeDocument(accreditationId, formData);

      toast.success("Document uploaded successfully!");

      // Reset form
      setTitle("");
      setSelectedFile(null);
      document.getElementById('file-input').value = '';

      // Refresh documents list
      fetchDocuments();
    } catch (error) {
      console.error("Upload error:", error);
      toast.error(error.message || "Failed to upload document");
    } finally {
      setIsUploading(false);
    }
  };

  const handleDelete = async (documentId) => {
    if (!window.confirm("Are you sure you want to delete this document?")) {
      return;
    }

    try {
      const { deleteRepresentativeDocument } = await import("../../service/templateService");
      await deleteRepresentativeDocument(accreditationId, documentId);

      toast.success("Document deleted successfully");
      fetchDocuments();
    } catch (error) {
      console.error("Delete error:", error);
      toast.error(error.message || "Failed to delete document");
    }
  };

  const handlePreview = (document) => {
    const apiBaseUrl = process.env.REACT_APP_API_URL || "http://localhost:5000";
    setPreviewDocument({
      name: document.title,
      description: `Uploaded on ${new Date(document.uploadDate).toLocaleDateString()}`,
      uri: `${apiBaseUrl}/download/${document.file.path}`,
      type: document.file.mimetype?.split('/').pop(),
    });
    setShowPreview(true);
  };

  const handleDownload = (document) => {
    const apiBaseUrl = process.env.REACT_APP_API_URL || "http://localhost:5000";
    const downloadUrl = `${apiBaseUrl}/download/${document.file.path}`;

    const link = window.document.createElement("a");
    link.href = downloadUrl;
    link.download = document.file.originalname;
    link.click();

    toast.success(`Downloading ${document.title}...`);
  };

  const getStatusBadge = (status) => {
    const badges = {
      uploaded: { bg: 'bg-blue-100', text: 'text-blue-800', border: 'border-blue-200', label: 'Uploaded' },
      reviewed: { bg: 'bg-yellow-100', text: 'text-yellow-800', border: 'border-yellow-200', label: 'Reviewed' },
      approved: { bg: 'bg-green-100', text: 'text-green-800', border: 'border-green-200', label: 'Approved' },
      rejected: { bg: 'bg-red-100', text: 'text-red-800', border: 'border-red-200', label: 'Rejected' },
    };

    const badge = badges[status] || badges.uploaded;

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded text-xs font-medium border ${badge.bg} ${badge.text} ${badge.border}`}>
        {badge.label}
      </span>
    );
  };

  if (!selectedProgramme) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="bg-yellow-50 rounded-lg border-2 border-yellow-200 p-8 text-center">
            <ExclamationTriangleIcon className="h-12 w-12 text-yellow-600 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-yellow-900 mb-2">
              No Programme Selected
            </h3>
            <p className="text-yellow-800">
              Please select a programme to upload documents.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Page Header */}
        <div className="bg-white rounded-xl shadow-sm border-2 border-blue-200 p-6">
          <div className="flex items-center space-x-3 mb-4">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center">
              <CloudArrowUpIcon className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Upload Documents</h1>
              <p className="text-gray-600 mt-1">
                Upload required programme documents for review
              </p>
            </div>
          </div>

          {/* Selected Programme Info */}
          {selectedProgramme && (
            <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
              <p className="text-sm font-semibold text-blue-900">
                Current Programme: {selectedProgramme.programmeName || "N/A"}
              </p>
              <p className="text-xs text-blue-700 mt-1">
                {selectedProgramme.institutionName || "N/A"}
              </p>
            </div>
          )}
        </div>

        {/* Stage 6 Warning Banner */}
        {isStage6 && (
          <div className="bg-yellow-50 border-2 border-yellow-300 rounded-xl p-4 mb-6">
            <div className="flex items-start space-x-3">
              <ExclamationTriangleIcon className="h-6 w-6 text-yellow-600 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="text-sm font-bold text-yellow-900 mb-1">
                  Accreditation Submitted (Stage 6)
                </h3>
                <p className="text-sm text-yellow-800">
                  This accreditation has been submitted. You cannot upload or delete documents at this stage.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Upload Form */}
        <div className="bg-white rounded-xl shadow-sm border-2 border-gray-200 p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Upload New Document</h2>
          {isStage6 && (
            <div className="text-center py-8 text-gray-500">
              <p className="text-sm">Document uploads are disabled for submitted accreditations.</p>
            </div>
          )}
          {!isStage6 && (
            <form onSubmit={handleUpload} className="space-y-4">
            <div>
              <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-2">
                Document Title *
              </label>
              <input
                type="text"
                id="title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Enter a descriptive title for your document"
                className="w-full px-4 py-3 text-base border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>

            <div>
              <label htmlFor="file-input" className="block text-sm font-medium text-gray-700 mb-2">
                Select File *
              </label>
              <div className="flex items-center space-x-4">
                <label className="flex-1 flex items-center justify-center px-4 py-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-400 cursor-pointer transition-all duration-200">
                  <CloudArrowUpIcon className="h-6 w-6 text-gray-400 mr-2" />
                  <span className="text-base text-gray-600">
                    {selectedFile ? selectedFile.name : 'Choose a file (PDF, Word, Excel, Image)'}
                  </span>
                  <input
                    id="file-input"
                    type="file"
                    className="hidden"
                    onChange={handleFileChange}
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
                    required
                  />
                </label>
              </div>
              {selectedFile && (
                <p className="text-sm text-gray-500 mt-2">
                  Size: {(selectedFile.size / (1024 * 1024)).toFixed(2)} MB
                </p>
              )}
            </div>

            <button
              type="submit"
              disabled={isUploading || !title.trim() || !selectedFile}
              className="w-full px-6 py-4 text-base font-medium text-white bg-gradient-to-r from-blue-600 to-blue-700 rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all duration-200 shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
            >
              {isUploading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span className="text-white">Uploading...</span>
                </>
              ) : (
                <>
                  <CloudArrowUpIcon className="h-5 w-5 text-white" />
                  <span className="text-white">Upload Document</span>
                </>
              )}
            </button>
          </form>
          )}
        </div>

        {/* Uploaded Documents List */}
        <div className="bg-white rounded-xl shadow-sm border-2 border-gray-200 p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">
            Uploaded Documents ({uploadedDocuments.length})
          </h2>

          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="w-16 h-16 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin mx-auto mb-4"></div>
                <p className="text-gray-600">Loading documents...</p>
              </div>
            </div>
          ) : uploadedDocuments.length > 0 ? (
            <div className="space-y-3">
              {uploadedDocuments.map((doc) => (
                <div
                  key={doc._id}
                  className="bg-gray-50 border-2 border-gray-200 rounded-lg p-4 hover:border-blue-300 transition-all duration-200"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-4 flex-1">
                      <div className="flex-shrink-0">
                        <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center">
                          <DocumentTextIcon className="h-5 w-5 text-white" />
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-2 mb-1">
                          <h3 className="text-base font-semibold text-gray-900">{doc.title}</h3>
                          {getStatusBadge(doc.status)}
                        </div>
                        <p className="text-sm text-gray-600 mb-2">
                          {doc.file.originalname} • {(doc.file.size / (1024 * 1024)).toFixed(2)} MB
                        </p>
                        <div className="flex items-center space-x-4 text-sm text-gray-500">
                          <span>Uploaded: {new Date(doc.uploadDate).toLocaleDateString()}</span>
                          {doc.uploadedBy && <span>By: {doc.uploadedBy.name}</span>}
                        </div>
                        {doc.reviewNotes && (
                          <p className="text-sm text-gray-600 mt-2 bg-white p-2 rounded border border-gray-200">
                            <span className="font-medium">Review Notes:</span> {doc.reviewNotes}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center space-x-2 ml-4">
                      <button
                        onClick={() => handlePreview(doc)}
                        className="p-2 text-green-700 bg-green-50 border-2 border-green-200 rounded-lg hover:bg-green-100 transition-all duration-200"
                        title="Preview document"
                      >
                        <EyeIcon className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => handleDownload(doc)}
                        className="p-2 text-blue-700 bg-blue-50 border-2 border-blue-200 rounded-lg hover:bg-blue-100 transition-all duration-200"
                        title="Download document"
                      >
                        <ArrowDownTrayIcon className="h-5 w-5" />
                      </button>
                      {doc.status === 'uploaded' && !isStage6 && (
                        <button
                          onClick={() => handleDelete(doc._id)}
                          className="p-2 text-red-700 bg-red-50 border-2 border-red-200 rounded-lg hover:bg-red-100 transition-all duration-200"
                          title="Delete document"
                        >
                          <TrashIcon className="h-5 w-5" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <DocumentTextIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No Documents Uploaded</h3>
              <p className="text-gray-600">Upload your first document using the form above.</p>
            </div>
          )}
        </div>

        {/* Document Preview Modal */}
        {showPreview && previewDocument && (
          <DocumentPreview
            isOpen={showPreview}
            onClose={() => {
              setShowPreview(false);
              setPreviewDocument(null);
            }}
            document={previewDocument}
            downloadUrl={previewDocument.uri}
          />
        )}
      </div>
    </div>
  );
};

export default UploadDocuments;
